import { element, requiredElement } from "../shared/dom";

interface Quantity {
  value: number | null;
  unit: string;
}

interface GuideData {
  overview: {
    purpose: string;
    plain_warning: string;
    metrics: Array<{ label: string; value: string; note: string }>;
  };
  bill_of_materials: {
    allowance_basis: {
      timber_percent: number;
      plywood_percent: number;
      insulation_percent: number;
      membrane_laps_percent: number;
    };
    items: Array<{
      category: string;
      item: string;
      specification: string;
      calculated: Quantity;
      planning: Quantity;
      status: string;
      notes: string;
    }>;
    unquantified: string[];
  };
  build_steps: Array<{
    number: number;
    title: string;
    summary: string;
    checks: string[];
    hold_point: boolean;
  }>;
  decision_points: Array<{
    title: string;
    why: string;
    next_action: string;
    status: string;
  }>;
}

type GuideSection = "overview" | "materials" | "steps" | "decisions";

export class ProjectGuide {
  #content: HTMLElement;
  #warning: HTMLElement;
  #tabs: HTMLButtonElement[];
  #active: GuideSection = "overview";
  #data: GuideData | undefined;
  #error: Error | undefined;
  #loadPromise: Promise<void> | undefined;

  constructor(dialog: HTMLDialogElement) {
    this.#content = requiredElement<HTMLElement>("#guideContent", dialog);
    this.#warning = requiredElement<HTMLElement>(".guide-warning", dialog);
    this.#tabs = [...dialog.querySelectorAll<HTMLButtonElement>("[data-guide-tab]")];
    for (const tab of this.#tabs) {
      tab.addEventListener("click", () => this.select(tab.dataset.guideTab as GuideSection));
      tab.addEventListener("keydown", (event) => this.#moveTabFocus(event, tab));
    }
  }

  load(url: URL): Promise<void> {
    if (this.#loadPromise) return this.#loadPromise;
    this.#loadPromise = fetch(url, { headers: { Accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Project guide request failed with HTTP ${response.status}`);
        this.#data = (await response.json()) as GuideData;
        this.#warning.textContent = this.#data.overview.plain_warning;
        this.render();
      })
      .catch((error: unknown) => {
        this.#error = error instanceof Error ? error : new Error(String(error));
        this.render();
      });
    return this.#loadPromise;
  }

  select(section: GuideSection): void {
    this.#active = section;
    for (const tab of this.#tabs) {
      tab.setAttribute("aria-selected", String(tab.dataset.guideTab === section));
      tab.tabIndex = tab.dataset.guideTab === section ? 0 : -1;
    }
    this.render();
    this.#content.scrollTop = 0;
  }

  render(): void {
    if (this.#error) {
      const error = element("div", { className: "guide-card" });
      error.append(element("h3", { text: "The generated project guide is unavailable" }));
      error.append(element("p", { text: "The 3D review is still available. Regenerate the package to restore materials, sequence and decision details." }));
      this.#content.replaceChildren(error);
      return;
    }
    if (!this.#data) return;
    if (this.#active === "materials") this.#renderMaterials();
    else if (this.#active === "steps") this.#renderSteps();
    else if (this.#active === "decisions") this.#renderDecisions();
    else this.#renderOverview();
  }

  #renderOverview(): void {
    const data = this.#data!;
    const fragment = document.createDocumentFragment();
    fragment.append(element("h3", { text: "Issued core proposal" }));
    fragment.append(element("p", { text: data.overview.purpose }));

    const grid = element("div", { className: "metric-grid" });
    for (const metric of data.overview.metrics) {
      const card = element("article", { className: "metric-card" });
      card.append(element("span", { text: metric.label }));
      card.append(element("strong", { text: metric.value }));
      card.append(element("span", { text: metric.note }));
      grid.append(card);
    }
    fragment.append(grid);
    fragment.append(element("h3", { text: "How to use this review" }));
    const list = element("ul");
    for (const item of [
      "Use Cutaway to compare the existing shell with the proposed interior layers.",
      "Use Materials for planning quantities, not a purchase order.",
      "Pause at every hold point before an irreversible step.",
      "Keep furnishing ideas separate from the issued core.",
    ]) list.append(element("li", { text: item }));
    fragment.append(list);
    this.#content.replaceChildren(fragment);
  }

  #renderMaterials(): void {
    const data = this.#data!;
    const allowance = data.bill_of_materials.allowance_basis;
    const fragment = document.createDocumentFragment();
    fragment.append(element("h3", { text: "Bill of materials — planning estimate" }));
    fragment.append(element("p", {
      text: `Allowances include ${allowance.timber_percent}% timber, ${allowance.plywood_percent}% plywood, ${allowance.insulation_percent}% insulation and ${allowance.membrane_laps_percent}% moisture-layer laps. Confirm products, stock lengths and layouts before ordering.`,
    }));
    const cards = element("div", { className: "card-grid" });
    for (const item of data.bill_of_materials.items) {
      const card = element("article", { className: "guide-card" });
      const top = element("div", { className: "guide-card-top" });
      const title = element("div");
      title.append(element("span", { className: `status-chip${isHold(item.status) ? " is-hold" : ""}`, text: item.category }));
      title.append(element("h4", { text: item.item }));
      top.append(title, element("strong", { className: "quantity", text: quantity(item.planning) }));
      card.append(top);
      card.append(element("p", { text: item.specification }));
      card.append(element("p", { text: `Model basis: ${quantity(item.calculated)}. ${item.notes}` }));
      cards.append(card);
    }
    fragment.append(cards);
    if (data.bill_of_materials.unquantified.length) {
      fragment.append(element("h3", { text: "Still to quantify" }));
      const list = element("ul");
      for (const item of data.bill_of_materials.unquantified) list.append(element("li", { text: item }));
      fragment.append(list);
    }
    this.#content.replaceChildren(fragment);
  }

  #renderSteps(): void {
    const fragment = document.createDocumentFragment();
    fragment.append(element("h3", { text: "Build order" }));
    fragment.append(element("p", { text: "Resolve each hold point before the next irreversible step." }));
    const cards = element("div", { className: "card-grid" });
    for (const step of this.#data!.build_steps) {
      const card = element("article", { className: "guide-card" });
      const top = element("div", { className: "guide-card-top" });
      const heading = element("div", { className: "step-heading" });
      heading.append(element("span", { className: "step-number", text: String(step.number) }));
      heading.append(element("h4", { text: step.title }));
      top.append(heading);
      if (step.hold_point) top.append(element("span", { className: "status-chip is-hold", text: "Hold point" }));
      card.append(top, element("p", { text: step.summary }));
      const checks = element("ul");
      for (const check of step.checks) checks.append(element("li", { text: check }));
      card.append(checks);
      cards.append(card);
    }
    fragment.append(cards);
    this.#content.replaceChildren(fragment);
  }

  #renderDecisions(): void {
    const fragment = document.createDocumentFragment();
    fragment.append(element("h3", { text: "Decisions and hold points" }));
    fragment.append(element("p", { text: "These items still affect safety, moisture performance, cost or how the core goes together." }));
    const cards = element("div", { className: "card-grid" });
    for (const decision of this.#data!.decision_points) {
      const card = element("article", { className: "guide-card" });
      const top = element("div", { className: "guide-card-top" });
      top.append(element("h4", { text: decision.title }));
      top.append(element("span", {
        className: `status-chip${isHold(decision.status) ? " is-hold" : ""}`,
        text: statusLabel(decision.status),
      }));
      card.append(top, element("p", { text: decision.why }));
      const action = element("p");
      action.append(element("strong", { text: "Next: " }), document.createTextNode(decision.next_action));
      card.append(action);
      cards.append(card);
    }
    fragment.append(cards);
    this.#content.replaceChildren(fragment);
  }

  #moveTabFocus(event: KeyboardEvent, current: HTMLButtonElement): void {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const index = this.#tabs.indexOf(current);
    const offset = event.key === "ArrowRight" ? 1 : -1;
    const next = this.#tabs[(index + offset + this.#tabs.length) % this.#tabs.length];
    next?.focus();
    next?.click();
  }
}

function quantity(value: Quantity): string {
  return value.value === null ? value.unit : `${value.value} ${value.unit}`;
}

function isHold(status: string): boolean {
  return status.includes("hold") || status.includes("before_");
}

function statusLabel(status: string): string {
  return status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
